/**
 * Internal data structure used by AnimationManager to track playing animations.
 * This mirrors the C++ AnimationPlayInfo class.
 */

import { AnimationPlayStyle } from './AnimationPlayStyle';
import { Animation } from './BaseAnimation';

/**
 * Callback function invoked when an animation completes.
 * @param animation - The animation that completed
 */
export type AnimationCompletionFunc = (animation: Animation) => void;

/**
 * Information the AnimationManager uses to manage an individual animation.
 * This is a direct port of the C++ AnimationPlayInfo struct.
 */
export class AnimationPlayInfo {
  /** The animation being managed */
  public animation: Animation;

  /** Timestamp when the animation started (in milliseconds) */
  public startTime: number;

  /** Total duration of the animation (in milliseconds) */
  public duration: number;

  /** The fraction the animation started from (usually 0, but can start mid-animation) */
  public startFraction: number;

  /** Whether the animation is playing forward (true) or backward (false) */
  public playForwards: boolean;

  /** The play style (OneShot, Loop, Rocker, Interactive) */
  public playStyle: AnimationPlayStyle;

  /** Current fraction for interactive animations */
  public currentInteractiveFraction: number;

  /** Previous fraction for interactive animations (for threshold detection) */
  public lastInteractiveFraction: number;

  /** Callback to invoke when the animation completes */
  public completionFunc?: AnimationCompletionFunc;

  /** Whether this is an interactive animation being cancelled */
  public isInteractiveCancel: boolean;

  /** Whether this animation was manually cancelled (shouldn't call completion callback) */
  public wasCancelled: boolean;

  /** Number of times this animation has played (for loops) */
  public playCount: number;

  /** Original duration before any modifications */
  public originalDuration: number;

  constructor(
    animation: Animation,
    startTime: number,
    duration: number,
    startFraction = 0,
    playForwards = true,
    playStyle: AnimationPlayStyle = AnimationPlayStyle.OneShot,
    completionFunc?: AnimationCompletionFunc
  ) {
    this.animation = animation;
    this.startTime = startTime;
    this.duration = duration;
    this.startFraction = startFraction;
    this.playForwards = playForwards;
    this.playStyle = playStyle;
    this.currentInteractiveFraction = startFraction;
    this.lastInteractiveFraction = startFraction;
    this.completionFunc = completionFunc;
    this.isInteractiveCancel = false;
    this.wasCancelled = false;
    this.playCount = 0;
    // Sentinel value -1 matches C++ pattern: indicates originalDuration hasn't been set yet
    // UpdateRockerAnimation uses this to detect the first tick and cap backward duration
    this.originalDuration = -1;
  }
}
